import { useState, useEffect } from "react";
import { useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import {
  useGetCallup,
  useUpdateCallup,
  getGetCallupQueryKey,
  getListLeagueCallupsQueryKey,
} from "@workspace/api-client-react";

export function EditCallupPage() {
  const { leagueId, callupId } = useParams<{ leagueId: string; callupId: string }>();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    dateTime: "",
    location: "",
    maxPlayers: "8",
  });

  const { data: callup, isLoading } = useGetCallup(leagueId ?? "", callupId ?? "", {
    query: { queryKey: getGetCallupQueryKey(leagueId ?? "", callupId ?? ""), enabled: !!leagueId && !!callupId },
  });

  useEffect(() => {
    if (callup) {
      setFormData({
        title: callup.title || "",
        description: callup.description || "",
        dateTime: callup.date ? new Date(callup.date).toISOString().slice(0, 16) : "",
        location: callup.location || "",
        maxPlayers: String(callup.maxPlayers || "8"),
      });
    }
  }, [callup]);

  const editCallupMutation = useUpdateCallup({
    mutation: {
      onSuccess: () => {
        if (leagueId && callupId) {
          queryClient.invalidateQueries({ queryKey: getGetCallupQueryKey(leagueId, callupId) });
          queryClient.invalidateQueries({ queryKey: getListLeagueCallupsQueryKey(leagueId) });
        }
        toast({ title: "Convocatoria actualizada" });
        history.back();
      },
      onError: (error: Error) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      },
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leagueId || !callupId) return;
    const parsedMax = parseInt(formData.maxPlayers) || 2;
    editCallupMutation.mutate({
      leagueId,
      callupId,
      data: {
        title: formData.title,
        description: formData.description || undefined,
        date: formData.dateTime,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        location: formData.location || undefined,
        maxPlayers: Math.max(2, parsedMax),
      },
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header
        className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="container max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => history.back()}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Editar Convocatoria</h1>
        </div>
      </header>

      <main className="container max-w-2xl mx-auto px-4 pt-3 safe-area-bottom">
        <p className="text-xs text-muted-foreground mb-3">
          Los campos marcados con <span className="text-destructive">*</span> son obligatorios.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="callup-title">
              Titulo <span className="text-destructive">*</span>
            </Label>
            <Input
              id="callup-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Ej: Partido del viernes"
              required
              data-testid="input-callup-title"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="callup-description">Descripcion (opcional)</Label>
            <Textarea
              id="callup-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Detalles adicionales..."
              rows={3}
              data-testid="input-callup-description"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="callup-datetime">
              Fecha y hora <span className="text-destructive">*</span>
            </Label>
            <Input
              id="callup-datetime"
              type="datetime-local"
              value={formData.dateTime}
              onChange={(e) => setFormData({ ...formData, dateTime: e.target.value })}
              required
              data-testid="input-callup-datetime"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="callup-location">Ubicacion (opcional)</Label>
            <Input
              id="callup-location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Ej: Club de Padel Central"
              data-testid="input-callup-location"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="callup-max-players">
              Jugadores maximos <span className="text-destructive">*</span>
            </Label>
            <Input
              id="callup-max-players"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formData.maxPlayers}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setFormData({ ...formData, maxPlayers: val });
              }}
              placeholder="Ej: 8"
              required
              data-testid="input-callup-max-players"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => history.back()}
              data-testid="button-cancel-callup"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={!formData.title.trim() || !formData.dateTime || !formData.maxPlayers || editCallupMutation.isPending}
              data-testid="button-submit-callup"
            >
              {editCallupMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Guardar Cambios
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
